/**
 * Renders the navigation component.
 * This component displays the navigation bar with brand, links, user avatar, and logout button.
 */
import { Option, Select } from "@mui/joy";
import {
	Avatar,
	Box,
	Divider,
	Drawer,
	IconButton,
	List,
	ListItemButton,
	ListItemText,
	Menu,
	MenuItem,
	Tooltip,
} from "@mui/material";
import { IoIosArrowDown } from "@react-icons/all-files/io/IoIosArrowDown";
import { IoClose } from "@react-icons/all-files/io5/IoClose";
import { signOut } from "firebase/auth";
import {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import {
	Link,
	useLocation,
	useNavigate,
} from "react-router-dom";
import { toast } from "react-toastify";

import { MenuIcon } from "../assets/icons/menuIcon";
import logo from "../assets/logos/blackblueLogo.svg";
import { auth } from "../firebase/firebaseConfig";
import {
	useAppDispatch,
	useAppSelector,
} from "../redux/store";
import { TenantActions } from "../redux/tenants/tenant.slice";
import { UserAuthActions } from "../redux/user/user.slice";
import { services } from "../services";
import { GlobalRoleEnum } from "../types/user.type";
import {
	stripTenantFromPathname,
	toTenantPath,
} from "../utils/tenantUtils";

const LLM_PROVIDER_LABELS: Record<string, string> = {
	gemini: "Gemini",
	claude: "Claude",
	openai: "OpenAI",
};

/**
 * Navigation component.
 */
export const Navigation = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	// Get user authentication status and information from Redux store
	const { user, isGlobalAdmin } = useAppSelector(
		(state) => state.user
	);
	const { allTenants, activeTenant } = useAppSelector(
		(state) => state.tenant
	);
	const [tenantList, setTenantList] = useState<Array<any>>(
		[]
	);
	const [mobileNavOpen, setMobileNavOpen] = useState(false);
	const tenantlessPathname = useMemo(
		() => stripTenantFromPathname(location.pathname),
		[location.pathname]
	);
	const getTenantPath = useCallback(
		(path: string, tenantId = activeTenant) =>
			toTenantPath(path, tenantId),
		[activeTenant]
	);

	useEffect(() => {
		if (allTenants) {
			const temp = Object.values(allTenants);
			setTenantList(temp);
		}
	}, [allTenants]);

	useEffect(() => {
		if (activeTenant) {
			dispatch(
				UserAuthActions.updateGlobalAdmin(
					activeTenant
				)
			);
		}
	}, [activeTenant, dispatch]);

	/**
	 * Handles the logout action.
	 * Signs out the user, dispatches logout action, and navigates to the login page.
	 */
	const handleLogout = useCallback(() => {
		signOut(auth)
			.then(() => {
				// Sign-out successful.
				dispatch(UserAuthActions.logout());
				navigate("/login");
			})
			.catch((error) => {
				console.error(error);
			});
	}, [dispatch, navigate]);

	const isPlatformAdmin =
		user?.global_role === GlobalRoleEnum.GLOBAL_ADMIN ||
		user?.global_role ===
			GlobalRoleEnum.GLOBAL_TENANT_ADMIN;
	const [llmProvider, setLlmProvider] = useState<
		string | null
	>(null);
	const [llmProviders, setLlmProviders] = useState<
		Array<string>
	>([]);

	const [anchorEl, setAnchorEl] =
		useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const handleClick = (
		event: React.MouseEvent<HTMLElement>
	) => {
		setAnchorEl(event.currentTarget);
		if (isPlatformAdmin) {
			services.agentServiceInstance
				.getPaulLlmProvider()
				.then(({ llm_provider, providers }) => {
					setLlmProvider(llm_provider);
					setLlmProviders(providers);
				})
				.catch((error) => {
					console.error(error);
				});
		}
	};
	const handleClose = () => {
		setAnchorEl(null);
	};
	const handleLlmProviderChange = (
		_event: React.SyntheticEvent | null,
		newValue: string | null
	) => {
		if (!newValue || newValue === llmProvider) {
			return;
		}
		services.agentServiceInstance
			.setPaulLlmProvider(newValue)
			.then(({ llm_provider, providers }) => {
				setLlmProvider(llm_provider);
				setLlmProviders(providers);
				const label =
					LLM_PROVIDER_LABELS[llm_provider] ??
					llm_provider;
				toast.success(`Paul now uses ${label}`);
			})
			.catch((error) => {
				console.error(error);
				toast.error("Couldn't change the Paul model");
			});
	};
	const handleChange = (
		event: React.SyntheticEvent | null,
		newValue: string | null
	) => {
		if (!newValue) {
			return;
		}

		const shouldResetPaulChatRoute =
			newValue !== activeTenant &&
			tenantlessPathname.startsWith("/agent/chat");

		dispatch(
			TenantActions.setActiveTenantReducer(
				newValue
			)
		);

		const nextPath = shouldResetPaulChatRoute
			? "/agent"
			: `${tenantlessPathname}${location.search}${location.hash}`;

		navigate(toTenantPath(nextPath, newValue), {
			replace: shouldResetPaulChatRoute,
		});
		setMobileNavOpen(false);
	};

	const allPaths = useMemo(() => {
		const paths = [
			{ path: "/", name: "Home" },
			{ path: "/agent", name: "Paul" },
			{ path: "/ledger", name: "Ledger" },
			{ path: "/lithe-library", name: "Lithe" },
			{ path: "/library", name: "Caliper" },
		];
		if (user?.global_role === GlobalRoleEnum.GLOBAL_ADMIN) {
			paths.push({ path: "/users", name: "Users" });
		}
		return paths;
	}, [user?.global_role]);

	return (
		<Box className="navigation">
			<Box className="brand">
				<img
					className="logo"
					src={logo}
					alt="Alloan"
					onClick={() => {
						navigate(getTenantPath("/"));
					}}
				/>
				<Box className="nav-links">
					{allPaths.map((path) => (
						<Link
							key={path.path}
							className={`link ${
								tenantlessPathname === path.path
									? "active"
									: ""
							}`}
							to={getTenantPath(path.path)}
						>
							{path.name}
						</Link>
					))}
				</Box>
			</Box>
			<Box className="actions">
				{tenantList.length > 1 ? (
					<Select
						className="tenant-dropdown desktop-tenant-dropdown"
						indicator={<IoIosArrowDown />}
						value={activeTenant}
						onChange={handleChange}
						disabled={tenantList.length <= 1}
					>
						{tenantList.map((tenant) => (
							<Option
								key={tenant.id}
								value={tenant.id}
								className="tenant-option"
							>
								{tenant.tenant_name}
							</Option>
						))}
					</Select>
				) : null}
				<Tooltip
					title="Account settings"
					sx={{
						cursor: "pointer",
					}}
				>
					<Box
						className={"avatar-box"}
						onClick={handleClick}
					>
						<Avatar
							sx={{
								width: 32,
								height: 32,
								background: "#4D2ED3",
								color: "white",
							}}
						>
							{user?.name.charAt(0)}
						</Avatar>
					</Box>
				</Tooltip>
				<IconButton
					className="mobile-nav-button"
					aria-label="Open navigation menu"
					onClick={() => {
						setMobileNavOpen(true);
					}}
				>
					<MenuIcon />
				</IconButton>
				<Menu
					anchorEl={anchorEl}
					id="account-menu"
					open={open}
					onClose={handleClose}
					onClick={handleClose}
					transformOrigin={{
						horizontal: "right",
						vertical: "top",
					}}
					anchorOrigin={{
						horizontal: "right",
						vertical: "bottom",
					}}
				>
					<MenuItem
						onClick={handleClose}
						className={"user-meta"}
						disabled={true}
					>
						<Avatar
							sx={{
								width: 36,
								height: 36,
								background: "#4D2ED3",
								color: "white",
							}}
						>
							{user?.name.charAt(0)}
						</Avatar>
						<Box className={"content"}>
							<h6>{user?.name}</h6>
							<p>{user?.email_id}</p>
						</Box>
					</MenuItem>
					<Divider />
					{isPlatformAdmin && llmProvider ? (
						<Box
							className="llm-provider-select"
							onClick={(e) => {
								e.stopPropagation();
							}}
							onKeyDown={(e) => {
								e.stopPropagation();
							}}
						>
							<p className="label">Paul model</p>
							<Select
								className="llm-provider-dropdown"
								value={llmProvider}
								onChange={handleLlmProviderChange}
								slotProps={{
									listbox: { disablePortal: true },
								}}
							>
								{llmProviders.map((provider) => (
									<Option
										key={provider}
										value={provider}
									>
										{LLM_PROVIDER_LABELS[provider] ??
											provider}
									</Option>
								))}
							</Select>
						</Box>
					) : null}
					{isGlobalAdmin ? (
						<MenuItem
							onClick={() => {
								navigate(
									getTenantPath(
										"/dataset-paul-sync"
									)
								);
							}}
						>
							Dataset Paul Sync
						</MenuItem>
					) : null}
					<MenuItem onClick={handleLogout}>Logout</MenuItem>
				</Menu>
			</Box>
			<Drawer
				anchor="right"
				open={mobileNavOpen}
				onClose={() => {
					setMobileNavOpen(false);
				}}
				className="mobile-navigation-drawer"
				PaperProps={{
					className: "mobile-navigation-drawer-paper",
				}}
			>
				<Box className="mobile-navigation-header">
					<img
						className="logo"
						src={logo}
						alt="Alloan"
						onClick={() => {
							navigate(getTenantPath("/"));
							setMobileNavOpen(false);
						}}
					/>
					<IconButton
						aria-label="Close navigation menu"
						onClick={() => {
							setMobileNavOpen(false);
						}}
					>
						<IoClose />
					</IconButton>
				</Box>
				{tenantList.length > 1 ? (
					<Box className="mobile-tenant-select">
						<Select
							className="tenant-dropdown"
							indicator={<IoIosArrowDown />}
							value={activeTenant}
							onChange={handleChange}
							disabled={tenantList.length <= 1}
						>
							{tenantList.map((tenant) => (
								<Option
									key={tenant.id}
									value={tenant.id}
									className="tenant-option"
								>
									{tenant.tenant_name}
								</Option>
							))}
						</Select>
					</Box>
				) : null}
				<List className="mobile-nav-links">
					{allPaths.map((path) => (
						<ListItemButton
							key={path.path}
							component={Link}
							to={getTenantPath(path.path)}
							className={`mobile-nav-link ${
								tenantlessPathname === path.path
									? "active"
									: ""
							}`}
							onClick={() => {
								setMobileNavOpen(false);
							}}
						>
							<ListItemText primary={path.name} />
						</ListItemButton>
					))}
				</List>
			</Drawer>
		</Box>
	);
};
